'use client'
import Typography from 'src/components/design-system/Typography'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Accordion from 'src/components/design-system/Acordion'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { joinSustainableDevelopmentGoalTitles } from 'src/_helpers/sustainableDevelopmentGoalTitle'
import {
  joinTranslatedTitles,
  JournalInfoGrid,
  JournalLicenseLinks,
  JournalLicenseModels,
  SearchFilterLinks,
} from 'src/components/journals/components/JournalHeaderShared'
import {
  buildPublicationDownloadEntries,
  DoiInfoLink,
  formatPublicationDate,
  PublicationAuthorsAccordion,
  PublicationDownloads,
  PublicationStatistics,
} from 'src/components/publications/PublicationHeaderShared'
import FormattedTitle from 'src/components/FormattedTitle'

const Header = ({ page, locale, routeContext = 'main' }) => {
  const translate = useTranslateEntry()
  const isDomainJournal = routeContext === 'journal-domain'
  const translation = getPreferredTranslation(page, locale)
  const title = <FormattedTitle value={translation?.title} />
  const doi = page.doi
  const doiUrl = page.doiUrl
  const licencesModels = page.licencesModels
  const licences = page.licenses
  const disciplines = page.disciplines
  const fields = page.fields
  const publicationLanguages = page.publicationLanguages
  const sustainableDevelopmentGoals = page.sustainableDevelopmentGoals
  const sustainableDevelopmentGoalsContent = joinSustainableDevelopmentGoalTitles(sustainableDevelopmentGoals, locale)
  const newestVolumeTitle = getPreferredTranslation(page.newestVolume, locale)?.title
  const issueTranslation = getPreferredTranslation(page.issue, locale) || {}
  const titleIssue = page.issue?.number ? `${translate('T_NUMBER')} ${page.issue.number}` : issueTranslation?.title
  const releaseYearIssue = page.issue?.releaseYear
  const publicationDate = page.publicationDate ? formatPublicationDate(page.publicationDate) : null
  const acceptanceDate = page.acceptanceDate ? formatPublicationDate(page.acceptanceDate) : null
  const submissionDate = page.submissionDate ? formatPublicationDate(page.submissionDate) : null
  const fallbackTitle = translate('T_FILE_NO_TITLE') || 'Brak tytułu'

  const entries = buildPublicationDownloadEntries(page.filesOpen, locale, fallbackTitle)
  const downloadLabel = translate('T_ARTICLE') || 'Pobierz'
  const infoItems = [
    {
      label: 'DOI',
      content: doi ? <DoiInfoLink doi={doi} href={doiUrl} fallback={doi} /> : null,
    },
    ...(publicationDate || submissionDate || acceptanceDate
      ? [
          { type: 'divider' },
          ...(publicationDate ? [{ label: translate('T_PUBLICATION_DATE'), content: publicationDate }] : []),
          ...(submissionDate ? [{ label: translate('T_SUBMISSION_DATE'), content: submissionDate }] : []),
          ...(acceptanceDate ? [{ label: translate('T_ACCEPTANCE_DATE'), content: acceptanceDate }] : []),
          { type: 'divider' },
        ]
      : []),
    {
      label: translate('T_LICENSE_MODEL'),
      content: joinTranslatedTitles(licencesModels, locale) ? (
        <JournalLicenseModels items={licencesModels} locale={locale} />
      ) : null,
    },
    {
      label: translate('T_LICENSE_TYPE'),
      content: joinTranslatedTitles(licences, locale) ? (
        <JournalLicenseLinks
          items={licences}
          locale={locale}
          linkClassName="block w-fit h-fit relative after:content-[''] after:h-1 after:w-full after:transition-[width] after:absolute after:bottom-0 after:left-0 hover:after:w-0 after:bg-[currentColor]"
        />
      ) : null,
    },
    { type: 'divider' },
    {
      label: translate('T_FIELD'),
      content: fields?.length ? <SearchFilterLinks items={fields} name="field" locale={locale} /> : null,
    },
    {
      label: translate('T_DISCIPLINE'),
      content: disciplines?.length ? <SearchFilterLinks items={disciplines} name="discipline" locale={locale} /> : null,
    },
    { type: 'divider' },
    ...(sustainableDevelopmentGoalsContent
      ? [
          {
            label: translate('T_SUSTAINABLE_DEVELOPMENT_GOALS'),
            content: sustainableDevelopmentGoalsContent,
          },
        ]
      : []),
    {
      label: translate('T_LANGUAGE_OF_PUBLICATION'),
      content: joinTranslatedTitles(publicationLanguages, locale),
    },
    { type: 'divider' },
  ]

  return (
    <header className={`flex flex-col gap-y-64`} id="article-publication-title">
      <div className="flex flex-col gap-y-24">
        <Typography variant="h1-bold" color="text-dark-blue-dark-blue1">
          {title}
        </Typography>
        <Typography variant="body1" color="text-dark-blue-dark-blue1">
          {releaseYearIssue && releaseYearIssue}
          {newestVolumeTitle && releaseYearIssue ? `, ${newestVolumeTitle}` : newestVolumeTitle}
          {titleIssue && (releaseYearIssue || newestVolumeTitle) ? `, ${titleIssue}` : titleIssue}
        </Typography>
      </div>
      <div className="flex flex-col gap-y-40">
        <PublicationAuthorsAccordion authors={page.authors} locale={locale} label={translate('T_AUTHORS')} />
        <Accordion type="block" color="dark-blue">
          <Accordion.Item>
            <Accordion.Trigger variant="button" icon="plus">
              {translate('T_BASIC_INFORMATION')}
            </Accordion.Trigger>
          </Accordion.Item>
          <Accordion.Content>
            <JournalInfoGrid items={infoItems} className="grid gap-x-40 gap-y-32 t:grid-cols-3 pt-24" />
          </Accordion.Content>
        </Accordion>
      </div>
      <div className="flex flex-col gap-y-40 pt-24">
        <PublicationDownloads
          entries={entries}
          title={translate('T_DOWNLOADS')}
          downloadLabel={downloadLabel}
          color={isDomainJournal ? 'journalDomain' : 'default'}
        />
        <PublicationStatistics statistics={page.statistics} color={isDomainJournal ? 'journalDomain' : 'default'} />
      </div>
    </header>
  )
}

export default Header
